
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { 
  AlertCircle,
  Check, 
  Clock, 
  History, 
  Loader, 
  User, 
  X
} from 'lucide-react';

import { 
  StatusHistoryEntry, 
  useDocumentStatusHistory 
} from '@/hooks/useDocumentStatusHistory';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';

interface DocumentStatusHistoryProps {
  documentId: string;
}

export function DocumentStatusHistory({ documentId }: DocumentStatusHistoryProps) {
  const { fetchStatusHistory, isLoading } = useDocumentStatusHistory(documentId);

  const { data: history = [] } = useQuery({
    queryKey: ['document-status-history', documentId],
    queryFn: fetchStatusHistory,
    enabled: !!documentId
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'approved':
        return <Check className="h-5 w-5 text-green-500" />;
      case 'rejected':
        return <X className="h-5 w-5 text-red-500" />;
      case 'review':
        return <Loader className="h-5 w-5 text-blue-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getInitials = (name?: string): string => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-medium">Status History</h3>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex gap-4">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-4 w-[100px]" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-[80px]" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-medium">Status History</h3>
        <div className="text-center py-10">
          <History className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No status history available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Status History</h3>
      
      <div className="relative pl-8 before:absolute before:left-4 before:top-0 before:h-full before:w-[1px] before:bg-border space-y-8">
        {history.map((entry) => (
          <div key={entry.id} className="relative">
            <div className="absolute -left-8 flex h-8 w-8 items-center justify-center rounded-full border bg-background">
              {getStatusIcon(entry.new_status)}
            </div>
            
            <div className="flex flex-col gap-2">
              <div className="flex items-center">
                <p className="font-medium">
                  Status changed to{' '}
                  <span className="capitalize font-semibold">
                    {entry.new_status}
                  </span>
                </p>
              </div>
              
              <div className="flex items-center gap-2">
                <Avatar className="h-6 w-6">
                  <AvatarFallback className="text-xs">
                    {getInitials(entry.user_details?.full_name)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm text-muted-foreground">
                  {entry.user_details?.full_name || 'User'} - {format(new Date(entry.changed_at), 'MMM d, yyyy h:mm a')}
                </span>
              </div>
              
              {entry.notes && (
                <p className="text-sm rounded-md bg-muted p-3 mt-2">
                  {entry.notes}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
